/**
 * MY STUDIO IS THE SOURCE. THIS IS THE ONLY PLACE THE PRO HUB READS IT FROM.
 *
 * Peter, 2026-08-26, seeing his own personal number in the Pro Hub footer and a
 * "Suite E" in a handbook address he never typed: "what I've been telling the
 * owners is that the My Studio is where we pull all of their information. So if
 * you're pulling information from anywhere else and it's not My Studio, we need
 * to stop and make sure, one, that you're pulling it from there, and if it's not
 * there, that we're putting it into My Studio."
 *
 * He was right about both, and the cause was one wrong table. My Studio writes
 * every field an owner edits to `location_kb_values` (ns='location') — that is
 * what studios-backend/supabase/functions/studio-profile/index.ts EDITABLE
 * declares, key for key. The Pro Hub was instead reading `location_configs`,
 * a DIFFERENT row that no owner edits, and the two had silently drifted:
 *
 *   address   My Studio "148 Old Post Road, Sharon, MA 02067"
 *             configs   "148 Old Post Road, Suite E, Sharon, MA 02067"   ← printed
 *   phone     My Studio support_phone 339-230-6833   (the studio's line)
 *             configs   phone         617-314-3772   ← shown to every pro
 *
 * So an owner could edit My Studio all day and the Pro Hub would never change.
 * Every pro-facing fact now comes through here, and adding a fact means adding
 * a key below — never a second read of another table.
 *
 * Readable by pros: verified against production as a real pro's own session
 * (Lisa Tester's uid, rolled back) — `location_kb_read` admits them through
 * app_can_access_location, 42 keys visible including support_phone.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { isCell } from '@/lib/cell/config';
import { cellGet } from '@/lib/cell/api';

/** The My Studio keys the Pro Hub shows. Each one is a field an owner edits. */
export interface StudioFacts {
  /** Studio identity */
  displayName: string | null;
  brandName: string | null;
  address: string | null;
  cityState: string | null;
  directions: string | null;
  website: string | null;
  instagram: string | null;
  /** Hours & contact — supportPhone is HOW PROS REACH THE STUDIO. */
  supportPhone: string | null;
  emergencyPhone: string | null;
  hoursOpen: string | null;
  hoursClose: string | null;
  hoursDays: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  /** Your documents */
  handbookUrl: string | null;
  securityDocEnabled: boolean;
  securityPolicyUrl: string | null;
  coiRequirementsUrl: string | null;
  /** The studio's OWN documents — My Studio's repeatable 'doclist' field.
   *  A row is EITHER an uploaded file (`path`, signed at view time) or a
   *  pasted link (`url`). Both sit on the same shelf. */
  studioDocs: Array<{ title: string; url?: string; path?: string }>;
  /** Community channel */
  communityEnabled: boolean;
  communityUrl: string | null;
  communityPlatform: string | null;
  /** Everything, for anything not yet named above. */
  raw: Record<string, string>;
}

/**
 * My Studio's 'doclist' — a JSON array of {title, url} in one value.
 * A malformed or hand-edited value yields an EMPTY list rather than throwing:
 * a pro's Documents page must not go blank because one field got mangled.
 * Rows missing both a title and a link are dropped; a row with a link but no
 * title still shows, named by its link, because the link is the useful half.
 */
export function parseDocList(raw: string | undefined): Array<{ title: string; url?: string; path?: string }> {
  if (!raw || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((r) => r && typeof r === 'object')
      .map((r) => ({
        title: String(r.title ?? '').trim(),
        url: r.url ? String(r.url).trim() : undefined,
        path: r.path ? String(r.path).trim() : undefined,
        name: r.name ? String(r.name).trim() : undefined,
      }))
      // A row that opens nothing is not a document.
      .filter((r) => r.url || r.path)
      .map((r) => ({ title: r.title || r.name || r.url || 'Document', url: r.url, path: r.path }));
  } catch {
    return [];
  }
}

const clean = (v: string | undefined): string | null => {
  const s = (v ?? '').trim();
  return s ? s : null;
};
// My Studio stores toggles as the literal 'yes' (studio-profile/index.ts isOn).
const on = (v: string | undefined): boolean => (v ?? '').trim().toLowerCase() === 'yes';

export function factsFromRows(rows: Array<{ key: string; value: string | null }>): StudioFacts {
  const m: Record<string, string> = {};
  for (const r of rows) if (r.value != null) m[r.key] = r.value;
  return {
    displayName: clean(m.display_name),
    brandName: clean(m.brand_name),
    address: clean(m.address),
    cityState: clean(m.location_city_state),
    directions: clean(m.location_directions),
    website: clean(m.website),
    instagram: clean(m.instagram),
    supportPhone: clean(m.support_phone),
    emergencyPhone: clean(m.emergency_phone),
    hoursOpen: clean(m.hours_open),
    hoursClose: clean(m.hours_close),
    hoursDays: clean(m.hours_days),
    ownerName: clean(m.owner_name),
    ownerEmail: clean(m.owner_reply_email),
    handbookUrl: clean(m.handbook_url),
    securityDocEnabled: on(m.security_doc_enabled),
    securityPolicyUrl: clean(m.security_policy_url),
    coiRequirementsUrl: clean(m.coi_requirements_url),
    studioDocs: parseDocList(m.studio_docs),
    communityEnabled: on(m.community_enabled),
    communityUrl: clean(m.community_url),
    communityPlatform: clean(m.community_platform),
    raw: m,
  };
}

export const EMPTY_FACTS: StudioFacts = factsFromRows([]);

/** One read, one cache, every pro-facing surface. */
export function useStudioFacts(locationId: string | null | undefined) {
  return useQuery({
    queryKey: ['studio-facts', locationId],
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<StudioFacts> => {
      // CELL (M28): GET /v1/pro-hub/locations/{locationId}/kb?ns=location — the same read (key, value).
      const { data, error } = isCell()
        ? await cellGet<{ rows: Array<{ key: string; value: string | null }> }>(`/v1/pro-hub/locations/${encodeURIComponent(locationId!)}/kb`, { ns: 'location' })
          .then((r) => ({ data: r.data?.rows ?? null, error: r.error }))
        : await supabase
          .from('location_kb_values')
          .select('key, value')
          .eq('location_id', locationId!)
          .eq('ns', 'location');
      // A failed read must not invent facts — and must not pose as a studio with none (HUB-071 /
      // HUB-074): it throws, and the caller (Documents) renders "could not load" with a retry.
      if (error) { console.error('My Studio facts read failed', error); throw new Error('Your studio’s details could not be read just now.'); }
      return factsFromRows(data ?? []);
    },
  });
}
